import { useMemo, useState } from 'react';
import { Progress } from '@heroui/react';
import cn from 'classnames';
import {
  isActiveScormUpload,
  scormUploads,
  useScormUploads,
  type StepScormUpload,
} from '@/features/StepScormUploads';
import {
  isActiveUpload,
  useVideoUploads,
  videoUploads,
  type StepVideoUpload,
} from '@/features/StepVideoUploads';
import { Svg } from '@/shared/ui/Svg';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';

type UploadKind = 'video' | 'scorm';

// Загрузка видео или SCORM-пакета шага в одном виде для трея
interface TrayUpload {
  key: string;
  kind: UploadKind;
  lessonTitle: string | null;
  stepNumber: number;
  fileName: string;
  fileSize: number;
  progress: number;
  isActive: boolean;
  isError: boolean;
  statusText: string;
  // Подпись повтора после ошибки; null — повтора нет
  retryLabel: string | null;
  // Подпись отмены: у идущей загрузки — Cancel, у ошибки — Remove или Dismiss; null — нельзя
  cancelLabel: string | null;
  retry: () => void;
  cancel: () => void;
}

const KIND_NOUNS: Record<UploadKind, [string, string]> = {
  video: ['video', 'videos'],
  scorm: ['SCORM package', 'SCORM packages'],
};

const getVideoStatusText = (upload: StepVideoUpload) => {
  switch (upload.status) {
    case 'queued':
      return 'Waiting...';
    case 'preparing':
      return 'Preparing...';
    case 'uploading':
      return `Uploading ${upload.progress}%`;
    case 'paused':
      return 'Waiting for connection...';
    case 'uploaded':
      return 'Uploaded, processing...';
    case 'error':
      return upload.error ?? 'Upload failed';
    default:
      return '';
  }
};

const getScormStatusText = (upload: StepScormUpload) => {
  switch (upload.status) {
    case 'queued':
      return 'Waiting...';
    case 'preparing':
      return 'Preparing...';
    case 'uploading':
      return `Uploading ${upload.progress}%`;
    case 'paused':
      return 'Waiting for connection...';
    case 'completing':
      return 'Finishing...';
    case 'uploaded':
      return 'Uploaded, processing...';
    case 'error':
      return upload.error ?? 'Upload failed';
    default:
      return '';
  }
};

const fromVideoUpload = (upload: StepVideoUpload): TrayUpload => {
  const isError = upload.status === 'error';
  const isActive = isActiveUpload(upload);
  return {
    key: `video-${upload.stepId}`,
    kind: 'video',
    lessonTitle: upload.lessonTitle,
    stepNumber: upload.stepNumber,
    fileName: upload.fileName,
    fileSize: upload.fileSize,
    progress: upload.progress,
    isActive,
    isError,
    statusText: getVideoStatusText(upload),
    retryLabel: isError ? (upload.canResume ? 'Resume' : 'Try again') : null,
    cancelLabel: isError ? 'Remove' : isActive ? 'Cancel' : null,
    retry: () => videoUploads.retry(upload.stepId),
    cancel: () => videoUploads.cancel(upload.stepId),
  };
};

const getScormRetryLabel = (upload: StepScormUpload) => {
  if (upload.status !== 'error' || !upload.retry) return null;
  return upload.retry === 'resume' ? 'Resume' : 'Try again';
};

const getScormCancelLabel = (upload: StepScormUpload) => {
  if (upload.status === 'error') return upload.ownsPackage ? 'Remove' : 'Dismiss';
  // Пока бэкенд собирает файл, отменять поздно: пакет уходит в обработку
  return isActiveScormUpload(upload) && upload.status !== 'completing' ? 'Cancel' : null;
};

const fromScormUpload = (upload: StepScormUpload): TrayUpload => ({
  key: `scorm-${upload.stepId}`,
  kind: 'scorm',
  lessonTitle: upload.lessonTitle,
  stepNumber: upload.stepNumber,
  fileName: upload.fileName,
  fileSize: upload.fileSize,
  progress: upload.progress,
  isActive: isActiveScormUpload(upload),
  isError: upload.status === 'error',
  statusText: getScormStatusText(upload),
  retryLabel: getScormRetryLabel(upload),
  cancelLabel: getScormCancelLabel(upload),
  retry: () => scormUploads.retry(upload.stepId),
  cancel: () => scormUploads.cancel(upload.stepId),
});

const pluralize = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

// Видео или пакеты, если все загрузки одного вида, иначе файлы
const countUploads = (uploads: TrayUpload[]) => {
  const kinds = new Set(uploads.map((upload) => upload.kind));
  const [one, many] = kinds.size === 1 ? KIND_NOUNS[uploads[0].kind] : ['file', 'files'];
  return pluralize(uploads.length, one, many);
};

// Доля отправленных байт по всем идущим загрузкам: большой файл весит больше маленького
const getOverallProgress = (uploads: TrayUpload[]) => {
  const totalBytes = uploads.reduce((sum, upload) => sum + upload.fileSize, 0);
  if (totalBytes === 0) return 0;
  const sentBytes = uploads.reduce(
    (sum, upload) => sum + (upload.fileSize * upload.progress) / 100,
    0,
  );
  return Math.round((sentBytes / totalBytes) * 100);
};

const getTitle = (active: TrayUpload[], failedCount: number) => {
  if (active.length > 0) {
    return `Uploading ${countUploads(active)} · ${getOverallProgress(active)}%`;
  }
  if (failedCount > 0) {
    return `${pluralize(failedCount, 'upload', 'uploads')} failed`;
  }
  return 'Uploads complete';
};

const TrayItem = ({ upload }: { upload: TrayUpload }) => {
  const step = `Step ${upload.stepNumber}`;
  // Загрузку узнают по уроку; без названия урока первой строкой идёт номер шага
  const title = upload.lessonTitle ?? step;
  const details = upload.lessonTitle ? `${step} · ${upload.fileName}` : upload.fileName;
  return (
    <li className="flex flex-col gap-1">
      <p className="truncate text-sm font-medium text-[#293056]" title={title}>
        {title}
      </p>
      <p className="truncate text-xs text-grey-secondary" title={details}>
        {details}
      </p>
      <Progress
        value={upload.progress}
        size="sm"
        aria-label={`${title}, ${details}: ${upload.progress}%`}
        classNames={{
          track: 'bg-[#ededed] h-[5px] rounded-[16px]',
          indicator: upload.isError
            ? 'bg-red-tertiary rounded-[16px]'
            : 'bg-[#211D55] rounded-[16px]',
        }}
      />
      <div className="flex items-start justify-between gap-3 text-xs">
        <span className={upload.isError ? 'text-red-tertiary' : 'text-grey-secondary'}>
          {upload.statusText}
        </span>
        <span className="flex shrink-0 gap-3">
          {upload.retryLabel && (
            <button
              type="button"
              className="cursor-pointer font-medium text-[#211D55] hover:underline"
              onClick={upload.retry}
            >
              {upload.retryLabel}
            </button>
          )}
          {upload.cancelLabel && (
            <button
              type="button"
              className="cursor-pointer text-red-tertiary hover:underline"
              onClick={upload.cancel}
            >
              {upload.cancelLabel}
            </button>
          )}
        </span>
      </div>
    </li>
  );
};

// Монтируется, пока есть загрузки: каждый раз, когда трей появляется заново, он свёрнут
const TrayPanel = ({ uploads }: { uploads: TrayUpload[] }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const active = uploads.filter((upload) => upload.isActive);
  const failedCount = uploads.filter((upload) => upload.isError).length;
  const hasOnlyFailures = active.length === 0 && failedCount > 0;

  return (
    <div
      className="fixed bottom-6 right-6 z-40 w-[360px] max-w-[calc(100vw-48px)] rounded-[16px] border border-gray-200 bg-white shadow-[0_12px_32px_rgba(33,29,85,0.18)]"
      role="region"
      aria-label="Uploads"
    >
      <button
        type="button"
        className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-black-headline"
        aria-expanded={!isCollapsed}
        onClick={() => setIsCollapsed((current) => !current)}
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className={cn('truncate', { 'text-red-tertiary': hasOnlyFailures })}>
            {getTitle(active, failedCount)}
          </span>
          {active.length > 0 && failedCount > 0 && (
            <span className="shrink-0 text-xs font-medium text-red-tertiary">
              {failedCount} failed
            </span>
          )}
        </span>
        <Svg
          Icon={ChevronDownIcon}
          width={20}
          height={20}
          className={cn('shrink-0 transition-transform', { 'rotate-180': isCollapsed })}
        />
      </button>
      {!isCollapsed && (
        <ul className="flex max-h-[50vh] flex-col gap-3 overflow-y-auto px-4 pb-4">
          {uploads.map((upload) => (
            <TrayItem key={upload.key} upload={upload} />
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Загрузки видео и SCORM-пакетов шагов, идущие в фоне: окно урока можно закрыть, а здесь видно,
 * что ещё грузится. Лежит под модалками (z-40), в открытом редакторе прогресс показывает карточка
 * шага. Появляется свёрнутым: в заголовке число загрузок, общий процент и ошибки, список — по клику.
 */
export const UploadsTray = () => {
  const videos = useVideoUploads();
  const packages = useScormUploads();
  const uploads = useMemo(
    () => [...videos.map(fromVideoUpload), ...packages.map(fromScormUpload)],
    [videos, packages],
  );
  if (uploads.length === 0) {
    return null;
  }
  return <TrayPanel uploads={uploads} />;
};
